// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es9:true*/
import ajax from '/util/ajax.js';
import etask from '/util/etask.js';
import zescape from '/util/escape.js';
import date from '/util/date.js';
import {with_cache, wrap_cp_url, wrap_url_prefix as wrap,
    notify_error} from './util.js';

const {MIN, SEC} = date.ms;
const uri = (raw, params)=>wrap(zescape.uri(raw, params));

export const get_billing_history = params=>etask(function(){
    return ajax.get(uri('/billing/history', params));
});

export const get_billing_invoices = partner_uuid=>etask(function(){
    return ajax.get(uri('/billing/invoices', {partner_uuid}));
});

export const get_billing_invoice_template_url = id=>
    uri(`/billing/invoices/${id}/template`);

export const attach_invoice_file = (payout_id, upload_id)=>etask(function(){
    return ajax.post(uri(`/billing/invoices/${payout_id}/attach`),
        {data: {upload_id}});
});

export const create_bill = data=>etask(function(){
    return ajax.post(uri('/billing/manage/bills/new'), {data});
});

export const get_balance = partner_uuid=>etask(function(){
    return ajax.get(uri('/billing/balance', {partner_uuid}));
});

export const get_all_bills = month=>etask(function(){
    return ajax.get(uri('/billing/manage/bills', {month}));
});

export const get_transactions = month=>etask(function(){
    return ajax.get(uri('/billing/manage/transactions', {month}));
});

export const get_payouts = opts=>etask(function(){
    return ajax.get(uri('/billing/manage/payouts', opts));
});

export const approve_payouts = ids=>etask(function(){
    return ajax.put(uri('/billing/manage/payouts/approve'), {data: {ids}});
});

export const notify_partners_payouts = ids=>etask(function(){
    return ajax.post(uri('/billing/manage/payouts/notify'), {data: {ids}});
});

export const set_payouts_paid = (ids, with_force)=>etask(function(){
    const endpoint = with_force
        ? '/billing/manage/payouts/force_paid'
        : '/billing/manage/payouts/paid';
    return ajax.put(uri(endpoint), {data: {ids}});
});

export const set_payouts_payable = ids=>etask(function(){
    return ajax.put(uri(`/billing/manage/payouts/payable`), {data: {ids}});
});

export const revert_payouts = ids=>etask(function(){
    return ajax.put(uri('/billing/manage/payouts/revert'), {data: {ids}});
});

export const get_balances = ()=>etask(function(){
    return ajax.get(uri('/billing/manage/balances'));
});

export const get_edit_overview = opts=>etask(function(){
    return ajax.get(uri('/billing/manage/edit_overview', opts));
});

export const edit_transaction = transaction=>etask(function(){
    const id = transaction._id;
    return ajax.put(uri(`/billing/manage/transactions/${id}`), {
        data: {
            amount: transaction.amount,
            created_ts: transaction.created_ts,
        },
    });
});

export const edit_payment = data=>etask(function(){
    return ajax.post(uri('/billing/manage/payment'), {data});
});

export const create_transaction = data=>etask(function(){
    return ajax.post(uri('/billing/manage/transactions/new'), {data});
});

export const get_top_sites = ()=>etask(function(){
    return ajax.get(uri(`/usage/top_sites`));
});

export const get_version = ()=>etask(function(){
    return ajax.get(uri(`/tools/version`));
});

export const get_rate_presets = ()=>etask(function(){
    return ajax.get(uri(`/tools/rate_presets`));
});

export const get_users = ()=>etask(function(){
    return ajax.get(uri(`/usrs`));
});

export const get_user = (params={})=>etask(function(){
    const {uuid, ...rest} = params;
    return ajax.get(uri(`/usrs/${uuid}`, rest));
});

export const edit_user = (params={})=>etask(function(){
    const {uuid, data} = params;
    return ajax.put(uri(`/usrs/${uuid}`), {data});
});

export const edit_user_perms = (params={})=>etask(function(){
    const {uuid, data} = params;
    return ajax.put(uri(`/usrs/${uuid}/permissions`), {data});
});

export const mark_user_analytics_done = event=>etask(function(){
    return ajax.put(uri(`/usrs/analytics/event/done`), {data: {event}});
});

export const transfer_user = (params={})=>etask(function(){
    const {user_uuid, target_uuid} = params;
    return ajax.put(uri(`/usrs/${user_uuid}/transfer`), {
        data: {target_uuid},
    });
});

export const get_partner = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}`));
});

export const get_partners_list = ()=>etask(function(){
    return ajax.get(uri(`/partners/list`));
});

export const get_partners_owners = ()=>etask(function(){
    return ajax.get(uri(`/partners/owners`));
});

export const get_owner_partners = owner_uuid=>etask(function(){
    return ajax.get(uri(`/partners/owners/${owner_uuid}/partners`));
});

export const get_new_partners = ()=>etask(function(){
    return ajax.get(uri('/partners/new_partners'));
});

export const get_partner_users = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/users`));
});

export const add_partner_user = (uuid, data)=>etask(function(){
    return ajax.post(uri(`/partners/partner/${uuid}/user`), {data});
});

export const kick_partner_user = (uuid, uid)=>etask(function(){
    return ajax.delete(uri(`/partners/partner/${uuid}/user/${uid}`));
});

export const get_partner_apps = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/apps`));
});

export const edit_partner = (uuid, data)=>etask(function(){
    return ajax.put(uri(`/partners/partner/${uuid}`), {data});
});

export const request_funds = (uuid, fund_kind, data)=>etask(function(){
    return ajax.post(
        uri(`/partners/partner/${uuid}/funds/${fund_kind}/request`), {data});
});

export const add_partner_billing = (uuid, data)=>etask(function(){
    return ajax.put(uri(`/partners/partner/${uuid}/billing`), {data});
});

export const remove_partner_billing = (uuid, billing_id, os)=>etask(function(){
    return ajax.delete(
        uri(`/partners/partner/${uuid}/billing/${billing_id}`, {os}));
});

export const get_partner_mongo = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/mongo`));
});

export const submit_kyc = data=>etask(function(){
    return ajax.put(uri(`/partners/kyc`), {data});
});

export const get_api_keys = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/api_keys`));
});

export const create_api_key = (uuid, data)=>etask(function(){
    return ajax.post(uri(`/partners/partner/${uuid}/api_key`), {data});
});

export const delete_api_key = (uuid, kuuid)=>etask(function(){
    return ajax.delete(uri(`/partners/partner/${uuid}/api_key/${kuuid}`));
});

export const get_partner_promo = ({uuid})=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/promo`));
});

export const apply_partner_promo = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    return ajax.post(uri(`/partners/partner/${uuid}/promo`), {data});
});

export const get_apps = (params={})=>etask(function(){
    return ajax.get(uri('/apps/list', params));
});

export const get_apps_cached = with_cache(get_apps, 5*MIN);

export const get_new_apps = ()=>etask(function(){
    return ajax.get(uri('/apps/new_apps'));
});

export const get_app = uuid=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}`));
});

export const edit_app = (uuid, data)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}`), {data});
});

export const add_app = data=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri('/apps/app/new'), {data});
});

export const add_app_billing = (uuid, data)=>etask(function(){
    return ajax.put(uri(`/apps/app/${uuid}/billing`), {data});
});

export const remove_app_billing = (uuid, billing_id)=>etask(function(){
    return ajax.delete(uri(`/apps/app/${uuid}/billing/${billing_id}`));
});

export const promote_all_apps_billing_est = uuid=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/billing/estimate_promote`));
});

export const promote_app_billing = (uuid, billing_id, data)=>etask(function(){
    return ajax.post(uri(`/apps/app/${uuid}/billing/${billing_id}/promote`),
        {data});
});

export const get_app_mongo = uuid=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/mongo`));
});

export const transfer_app = (uuid, recipient_uuid)=>etask(function(){
    get_apps_cached.flush_cache();
    const recipient = recipient_uuid;
    return ajax.put(uri(`/apps/app/${uuid}/transfer`), {data: {recipient}});
});

export const remove_app = uuid=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.delete(uri(`/apps/app/${uuid}`));
});

export const clone_app = (uuid, new_id)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.post(uri(`/apps/app/${uuid}/clone`), {data: {new_id}});
});

export const clone_bundle = (uuid, new_name)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.post(uri(`/apps/app/${uuid}/clone_bundle`),
        {data: {new_name}});
});

export const submit_app = (uuid, data)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}/submit`), {data});
});

export const get_reviews = app_uuid=>etask(function(){
    return ajax.get(uri('/reviews/list', {app_uuid}));
});

export const get_reviews_cached = with_cache(get_reviews, 1*MIN);

export const get_review = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    return ajax.get(uri(`/reviews/review/${uuid}`, data));
});

export const start_app_review = uuid=>etask(function(){
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/start`));
});

export const comment_app_review = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/comment`), {data});
});

export const submit_app_review = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/submit`), {data});
});

export const draft_review_checklist = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    return ajax.put(uri(`/reviews/review/${uuid}/submit/draft`), {data});
});

export const reject_app_review = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/reject`), {data});
});

export const approve_app_review = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/approve`), {data});
});

export const set_app_review_priority = params=>etask(function(){
    const {uuid, ...data} = params;
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/priority`), {data});
});

export const revert_app_review = uuid=>etask(function(){
    get_reviews_cached.flush_cache();
    return ajax.put(uri(`/reviews/review/${uuid}/revert`));
});

export const save_review_submit_form = (params={})=>etask(function(){
    const {uuid, ...data} = params;
    return ajax.post(uri(`/apps/app/${uuid}/submit/draft`), {data});
});

const ABORT_LABEL = '$aborted$';
const FATAL_UPLOAD_ERROR = 'Error while uploading file. Try again later.';
const DIRECT_S3_UPLOAD_ERROR = 'Error while uploading file. Try again later.';

export const upload = (files, {uploader, on_progress, on_uploaded})=>
etask(function*(){
    const result = {done: {}, errors: {}, canceled: {}};
    for (const file of files)
    {
        let meta_res;
        try {
            meta_res = yield file.get_meta();
        } catch(e){
            console.error(e.message);
            result.errors[file.fingerprint] = FATAL_UPLOAD_ERROR;
            continue;
        }
        if (meta_res.error)
        {
            result.errors[file.fingerprint] = meta_res.error;
            continue;
        }
        on_progress?.(file, 0);
        let upload_res;
        try {
            upload_res = yield uploader(file, meta_res.upload_meta,
                on_progress);
        } catch(e){
            console.error(e.message);
            result.errors[file.fingerprint] = DIRECT_S3_UPLOAD_ERROR;
            continue;
        }
        if (upload_res==ABORT_LABEL)
        {
            result.canceled[file.fingerprint] = true;
            continue;
        }
        on_progress?.(file, 100);
        yield on_uploaded?.(file, meta_res.id);
        result.done[file.fingerprint] = meta_res.id;
    }
    return result;
});

upload.s3 = (file, upload_meta, on_progress)=>etask(function(){
    const data = new FormData();
    for (const key of Object.keys(upload_meta.fields))
        data.append(key, upload_meta.fields[key]);
    data.append('file', file.blob);
    let task;
    const onuploadprogress = e=>{
        on_progress?.(file, e.loaded/e.total*100, task);
    };
    task = ajax.json({
        url: upload_meta.url,
        method: 'POST',
        multipart: true,
        timeout: 20*MIN,
        slow: 10*MIN,
        data,
        onuploadprogress,
        abort_value: ABORT_LABEL,
    });
    return task;
});

export const prepare_upload = (blob, params={})=>etask(function(){
    if (!params.kind)
        throw new Error('Attachment kind is required');
    return ajax.post(uri('/uploads/prepare'), {
        data: {
            filename: blob.name,
            last_modified: blob.lastModified ?? 1,
            ...params,
        },
    });
});

export const get_uploads_media_list = ()=>etask(function(){
    return ajax.get(uri(`/uploads/media_list`));
});

export const get_download_url = attah_id=>etask(function(){
    return ajax.get(uri(`/uploads/${attah_id}/read_url`));
});

export const get_download_url_cached = with_cache(get_download_url, 10*MIN);

export const get_upload_meta = attah_id=>etask(function(){
    return ajax.get(uri(`/uploads/${attah_id}/meta`));
});

export const get_upload_meta_cached = with_cache(get_upload_meta, 5*MIN);

export const set_uploaded = attah_id=>etask(function(){
    return ajax.put(uri(`/uploads/${attah_id}/uploaded`));
});

export const set_upload_clean = ({uuid})=>etask(function(){
    return ajax.put(uri(`/uploads/${uuid}/clean`));
});

export const disable_app = uuid=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}/disable`));
});

export const make_app_live = (uuid, url, verified_version)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}/make_live`),
        {data: {url, verified_version}});
});

export const make_app_implementation = uuid=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}/to_implementation`));
});

export const approve_app = (uuid, comment)=>etask(function(){
    get_apps_cached.flush_cache();
    return ajax.put(uri(`/apps/app/${uuid}/approve`), {data: {comment}});
});

export const get_app_checklist = uuid=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/checklist`));
});

export const get_review_checklist = uuid=>etask(function(){
    return ajax.get(uri(`/reviews/review/${uuid}/checklist`));
});

export const get_checklist_templates = uuid=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/checklist_templates`));
});

export const get_app_channels = ({uuid})=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/channels`));
});

export const get_checklist = (os, checklist_id)=>etask(function(){
    return ajax.get(uri(`/checklists/checklist/${os}/${checklist_id}`));
});

export const get_checklists = ()=>etask(function(){
    return ajax.get(uri('/checklists/list'));
});

const maybe_true = val=>val ? true : undefined;

const gather_stat_params = params=>({
    extrapolate: maybe_true(params.extrapolate),
    with_money: maybe_true(params.with_money),
    all_apps: maybe_true(params.all_apps),
    app_id: params.app_id,
    partner_id: params.partner_id,
    app_uuid: params.app_uuid,
    partner_uuid: params.partner_uuid,
    from: params.from,
    to: params.to,
    is_root: params.is_root,
    country: params.country,
    os: params.os,
    cn_limit: params.cn_limit,
});

export const get_stats_daily = params=>etask(function(){
    return ajax.get(uri('/stats/basic/daily_flat',
        gather_stat_params(params)));
});
export const get_stats_daily_cached = with_cache(get_stats_daily);

export const get_stats_daily_by_cn = params=>etask(function(){
    return ajax.get(uri('/stats/basic/daily_by_cn',
        gather_stat_params(params)));
});
export const get_stats_daily_by_cn_cached = with_cache(get_stats_daily_by_cn);

export const get_stats_daily_by_app = params=>etask(function(){
    return ajax.get(uri('/stats/basic/daily_by_app',
        gather_stat_params(params)));
});
export const get_stats_daily_by_app_cached =
    with_cache(get_stats_daily_by_app);

export const get_os_stats = ()=>etask(function(){
    return ajax.get(uri('/stats/os_stats'));
});

export const get_stats_overview = params=>etask(function(){
    return ajax.get(uri('/stats/overview', gather_stat_params(params)), {
        slow: 5*SEC,
    });
});
export const get_stats_overview_cached = with_cache(get_stats_overview);

export const get_basic_chart = params=>etask(function(){
    return ajax.get(uri('/stats/basic/chart', gather_stat_params(params)), {
        slow: 20*SEC,
    });
});
export const get_basic_chart_cached = with_cache(get_basic_chart);

export const get_basic_by_apps_legacy = params=>etask(function(){
    return ajax.get(uri('/stats/basic/folded_by_app_legacy',
        gather_stat_params(params)));
});
export const get_basic_by_apps_legacy_cached =
    with_cache(get_basic_by_apps_legacy);

export const get_basic_by_partners = params=>etask(function(){
    return ajax.get(uri('/stats/basic/folded_by_partner',
        gather_stat_params(params)));
});
export const get_basic_by_partners_cached = with_cache(get_basic_by_partners);

export const get_funnels = params=>etask(function(){
    return ajax.get(uri('/stats/funnels', gather_stat_params(params)), {
        timeout: 1*MIN,
    });
});

export const get_retention = params=>etask(function(){
    return ajax.get(uri('/stats/retention', gather_stat_params(params)), {
        timeout: 1*MIN,
    });
});

export const get_retention_date = ()=>etask(function(){
    return ajax.get(uri('/stats/retention_date'));
});

export const get_summary_metrics = params=>{
    return ajax.get(uri('/stats/summary_metrics', gather_stat_params(params)));
};

export const get_commission = params=>{
    return ajax.get(uri('/stats/commission', params));
};

export const get_qualified = params=>etask(function(){
    return ajax.get(uri('/stats/qualified', gather_stat_params(params)), {
        timeout: 1*MIN,
    });
});

export const get_commission_cached = with_cache(get_commission, 5*MIN);

export const verify_self_check_step = (uuid, step_id)=>etask(function(){
    return ajax.put(uri(`/apps/app/${uuid}/verify_step`, {step_id}), {
        slow: 45*SEC,
    });
});

export const check_app_sdk_version = (uuid, step_id)=>etask(function(){
    return ajax.get(uri(`/apps/app/${uuid}/sdk_ver_check`, {step_id}), {
        slow: 45*SEC,
    });
});

export const skip_app_check_step = (uuid, step_id, comment)=>etask(function(){
    return ajax.put(uri(`/apps/app/${uuid}/skip_check_step`), {
        data: {step_id, comment},
    });
});

export const global_search = query=>etask(function(){
    return ajax.post(uri('/search/global'), {data: {query}});
});

export const post_notif = data=>etask(function(){
    return ajax.post(uri('/notifs/new'), {data});
});

const clean_empty_keys = obj=>{
    for (const key in obj)
    {
        const v = obj[key];
        if ({'string': 1, 'number': 1, 'boolean': 1}[typeof v])
            continue;
        if (!v)
            delete obj[key];
    }
    return obj;
};

export const edit_notif = (uuid, data)=>etask(function(){
    return ajax.put(uri(`/notifs/${uuid}`), {data: clean_empty_keys(data)});
});

export const get_notifs = ()=>etask(function(){
    return ajax.get(uri('/notifs/list'));
});

export const estimate_notif_audience = target=>etask(function(){
    return ajax.post(uri('/notifs/estimate_audience'), {data: {target}});
});

export const get_my_notifs = (opts={})=>etask(function(){
    const {include_read, include_dismissed, include_outdated} = opts;
    return ajax.get(uri('/notifs/my', clean_empty_keys({
        include_read,
        include_dismissed,
        include_outdated,
    })));
});

export const dismiss_notif = uuid=>etask(function(){
    return ajax.put(uri(`/notifs/${uuid}/dismiss`));
});

export const read_notif = uuid=>etask(function(){
    return ajax.put(uri(`/notifs/${uuid}/read`));
});

export const get_url_status = data=>etask(function(){
    return ajax.post(uri('/urls/status'), {data});
});

export const signup = data=>etask(function(){
    return ajax.post(uri('/users/auth/basic/signup', {ajax: 1}),
        {data, json: false});
});

export const login2 = data=>etask(function(){
    return ajax.post(uri('/users/auth/basic2/login'), {data});
});

export const reset_pass = email=>etask(function(){
    return ajax.post(uri('/users/send_reset_password_mail'), {data: {email}});
});

export const get_apkids_legacy = q_params=>etask(function(){
    return ajax.get(uri('/sdk/apkids', q_params));
});

export const get_apkids_legacy_cached = with_cache(get_apkids_legacy, 5*MIN);

export const get_urls_legacy = q_params=>etask(function(){
    return ajax.get(uri('/sdk/urls', q_params));
});

export const get_urls_legacy_cached = with_cache(get_urls_legacy, 5*MIN);

export const get_versions_legacy = q_params=>etask(function(){
    return ajax.get(uri('/sdk/versions', q_params));
});

export const get_versions_legacy_cached =
    with_cache(get_versions_legacy, 5*MIN);

export const get_templates_legacy = q_params=>etask(function(){
    return ajax.get(uri('/sdk/templates', q_params));
});

export const get_templates_legacy_cached =
    with_cache(get_templates_legacy, 5*MIN);

export const get_changelog_legacy = ()=>etask(function(){
    return ajax.get(uri('/sdk/changelog'));
});

export const get_changelog_legacy_cached =
    with_cache(get_changelog_legacy, 5*MIN);

export const save_integration_legacy = (q_params, values)=>etask(function(){
    return ajax.post(uri('/sdk/integration', q_params), {data: {values}});
});

export const get_partner_integration_cfg = uuid=>etask(function(){
    return ajax.get(uri(`/partners/partner/${uuid}/integration_config`));
});

export const get_partner_integration_cfg_cached =
    with_cache(get_partner_integration_cfg, 5*MIN);

export const get_contest_stats = (params={})=>etask(function(){
    const {contest_id, ...rest} = params;
    return ajax.get(uri(`/contests/contest/${contest_id}/stats`, rest));
});

export const get_contest_leaderboard = (params={})=>etask(function(){
    const {contest_id, ...rest} = params;
    return ajax.get(uri(`/contests/contest/${contest_id}/leaderboard`, rest));
});

export const save_contest_stats = (params={})=>etask(function(){
    const {contest_id, ...data} = params;
    return ajax.put(uri(`/contests/contest/${contest_id}/stats`), {data});
});

export const review_report_url = uri('/reviews/report/basic');

export const abort = ajax.abort;

export const sign_out = ()=>etask(function*(){
    const res = yield ajax.post(uri('/users/logout/inline'));
    if (res?.error)
        return notify_error(res.error);
    document.location = wrap_cp_url('');
});

export const get_referrals_earnings = params=>etask(function(){
    return ajax.get(uri('/referrals/earnings', params), {
        slow: 5*SEC,
    });
});
export const get_referrals_earnings_cached =
    with_cache(get_referrals_earnings);

export const get_referrals_stats = params=>etask(function(){
    return ajax.get(uri('/referrals/stats', params), {
        slow: 5*SEC,
    });
});
export const get_referrals_stats_cached = with_cache(get_referrals_stats);

export const ref_agree = params=>etask(function(){
    return ajax.post(uri('/referrals/accept-ref-terms', params), {
        slow: 5*SEC,
    });
});

ajax.events.on('unauthorized', ()=>document.location.reload());
